import { GroupSubTypes } from '~/utils/Constants'
import { OWAuthDataModel } from '~/models/OWAuthDataModel'
import * as OWUtils from '~/utils/OWUtils'
import { staticPath } from '~/utils/OWUtils'
import { EmployeeSearchIndex } from '~/models/EmployeeSearchIndex'
import { ServerApi } from '~/utils/ServerApi'
import { OWApp } from '~/app/OWApp'
import { Employee, Group, Mention, MentionableText, MentionTypeaheadType } from '~/app/types'
import { TFunction } from 'i18next'
import { Global } from '~/global'
import { CustomerTeamMemberType, CustomerType } from '~/generated/types'
import { CustomerSearchIndex } from '~/models/CustomerSearchIndex'
import { urlForCustomer } from '~/utils/PlaceManager'
import { asCustomerType } from '~/react/utils/EmployeeUtils'

const { COMPANY_ID, IS_GHOST } = Global

export const getMentions = async (
    query: string,
    group?: Group,
    allowCompany?: boolean,
    searchingCustomer?: boolean
) => {
    if (searchingCustomer) return getCustomerMentions(query)
    return getEmployeeMentions(query, group, allowCompany)
}

const getCustomerMentions = async (query: string) => {
    const vals = CustomerSearchIndex.getInstance().search(query)
    if (vals.length > 1) {
        return vals.map(getMentionCustomer)
    } else {
        const response = await ServerApi.searchCustomers(7, undefined, query)
        return response.map(c => getMentionCustomer(asCustomerType(c)))
    }
}

const getEmployeeMentions = async (query: string, group?: Group, allowCompany?: boolean) => {
    const vals = EmployeeSearchIndex.getInstance().search(query)
    let employees: MentionTypeaheadType[]
    if (vals.length > 1) {
        employees = vals.map(getMentionEmployee)
    } else {
        const response = await ServerApi.searchEmployees(7, undefined, query)
        employees = response.map(getMentionEmployee)
    }
    const t = OWApp.getInstance().i18n?.t
    const preResults: MentionTypeaheadType[] = []
    if (t && group && group.sub_type !== GroupSubTypes.IN_MEMORIAM) {
        preResults.push(getMentionGroup(group, t))
    } else if (t && allowCompany && canMentionCompany()) {
        preResults.push(getMentionCompany(t))
    }
    return preResults.concat(employees)
}

const getMentionGroup = (group: Group, t: TFunction): MentionTypeaheadType => {
    const { id, name, group_photo_thumbnail } = group
    return {
        mention_id: String(id), // TODO: Is this correct?
        mention_token: 'group',
        mention_picture: group_photo_thumbnail || staticPath('groups/img/icon-list-community-lg.png'),
        mention_title: 'Group',
        mention_sub_title: t('mentions.group_notify', { name }),
        mention_object_name: 'Group',
        mention_object_lookup_name: 'id',
        mention_object_sort_order: 0,
    }
}

const getMentionCompany = (t: TFunction): MentionTypeaheadType => {
    const company = OWAuthDataModel.getAuthCompany()
    const name = company ? company.name : ''
    return {
        mention_id: COMPANY_ID,
        mention_token: 'all',
        mention_picture: staticPath('orgwiki/img/company-wide-badge-blue.png'),
        mention_title: 'All',
        mention_sub_title: t('mentions.company_notify', { name }),
        mention_object_name: 'Company',
        mention_object_lookup_name: 'id',
        mention_object_sort_order: 0,
    }
}

const canMentionCompany = () => {
    const employee = OWAuthDataModel.getAuthEmployee()
    return IS_GHOST || employee?.can_create_company_mentions
}

const getMentionEmployee = (employee: Employee): MentionTypeaheadType => {
    const { public_id, profile_photo_thumbnail, full_name, title } = employee
    return {
        mention_id: public_id,
        mention_token: full_name,
        mention_picture: profile_photo_thumbnail,
        mention_title: full_name,
        mention_sub_title: title,
        mention_object_name: 'Employee',
        mention_object_lookup_name: 'public_id',
        mention_object_sort_order: 1,
    }
}

const getMentionCustomer = (customer: CustomerType): MentionTypeaheadType => {
    const { customerId, name, logo } = customer
    return {
        mention_id: customerId,
        mention_token: name,
        mention_title: name,
        mention_picture: logo || OWUtils.staticPath('orgwiki/img/default-customer-photo.svg'),
        mention_object_name: 'Customer',
        mention_object_lookup_name: 'customerId',
        mention_object_sort_order: 1,
        mention_aliases:
            customer.aliases?.reduce((accumulator: string, currentValue) => {
                return accumulator + currentValue + ' '
            }, '') ?? '',
    }
}

type MentionUploadType = {
    object_name: string
    object_id: string
    object_lookup_field_name: string
}

export const parseMentions = (text: string) => {
    const mentionList = new Array<MentionUploadType>()
    let textCopy = text
    // @[@First Last](first_last:Employee),
    // match first group "first_last", second group "Employee"
    Array.from(textCopy.matchAll(/@\[@[^\]]+]\(([^)]+):([^)]+)\)/gm)).map((match, index) => {
        mentionList.push({
            object_name: match[2],
            object_id: match[1],
            object_lookup_field_name: match[2] === 'Employee' ? 'public_id' : 'id',
        })
        textCopy = textCopy.replace(match[0], `{${index}}`)
    })

    let mentionsObj
    if (mentionList.length > 0) {
        mentionsObj = {
            mentions: mentionList,
        }
    }
    return { text: textCopy, mentionsObj }
}

export const convertTextFromMentions = (text?: MentionableText) => {
    if (!text?.text) return ''
    let txt = text.text
    text.mentions.forEach((m, index) => {
        txt = txt.replace(
            `{${index}}`,
            `@[@${m.mention_text_name}](${m.mention_lookup_field_value}:${m.mention_object_name})`
        )
    })
    return txt
}

export const mapMentions = (mentions: Mention[]): MentionTypeaheadType[] => {
    return mentions.map(m => ({
        mention_object_name: m.mention_object_name,
        mention_id: m.mention_lookup_field_value,
        mention_object_lookup_name: m.mention_lookup_field_name,
        mention_title: m.mention_text_name,
    }))
}

export const getMentionableTextLength = (value?: string) => {
    if (!value) return 0
    return value.replaceAll(/@\[@([^\]]+)]\(([^)]+):([^)]+)\)/gm, `@$1`).length
}

export const createCustomerMentions = (customers: CustomerTeamMemberType[]): Mention[] => {
    return customers.map(c => {
        return {
            mention_text_name: c.customer.name,
            mention_lookup_field_name: 'customerId',
            mention_lookup_field_value: c.customerId.toString(),
            mention_object_name: 'customer',
            mention_object_url: urlForCustomer(c.customerId),
            is_executive_sponsor: c.isExecutiveSponsor,
            is_commercial_lead: c.isCommercialLead,
            is_rd_lead: c.isRdLead,
            is_account_lead: c.isAccountLead,
            logo: c.customer.logo,
        } as Mention
    })
}

export const createCustomerText = (customers: CustomerTeamMemberType[]): string => {
    return customers.reduce((accumulator: string, _, i) => {
        return accumulator + `{${i}}`
    }, '')
}

export const CUSTOMER_PILL_ID_PREFIX = 'customer-pill-id-'
export const getPillCustomerId = (str: string): number | undefined => {
    try {
        return +str.slice(CUSTOMER_PILL_ID_PREFIX.length)
    } catch {
        return undefined
    }
}
